import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, ArrowRight, ArrowLeft, Upload, Zap,
  MessageSquare, FolderOpen, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIALITES = [
  'Droit des affaires', 'Droit pénal', 'Droit social / travail',
  'Droit immobilier', 'Droit fiscal', 'Droit de la famille',
  'Droit public', 'Propriété intellectuelle', 'Droit européen', 'Autre',
];

const LEGAL_DOMAINS = [
  { id: 'droit_affaires',        label: 'Droit des affaires' },
  { id: 'droit_social',          label: 'Droit social' },
  { id: 'rgpd',                  label: 'RGPD / Protection des données' },
  { id: 'contentieux_civil',     label: 'Contentieux civil' },
  { id: 'droit_fiscal',          label: 'Droit fiscal' },
  { id: 'droit_penal',           label: 'Droit pénal' },
  { id: 'droit_public',          label: 'Droit public / Administratif' },
  { id: 'propriete_intellectuelle', label: 'Propriété intellectuelle' },
];

const ProgressBar = ({ step, total }) => (
  <div className="flex gap-1 mb-8">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
          i + 1 <= step ? 'bg-blue-600' : 'bg-gray-100'
        }`}
      />
    ))}
  </div>
);

const Onboarding = ({ user, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [specialites, setSpecialites] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [loading, setLoading] = useState(false);

  const TOTAL_STEPS = 5;

  const toggleSpecialite = (s) =>
    setSpecialites((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  const toggleDomain = (id) =>
    setSelectedDomains((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const completeOnboarding = async (redirectPath = '/dashboard') => {
    setLoading(true);
    const token = localStorage.getItem('lawliet_token');
    const headers = { Authorization: `Bearer ${token}` };

    // Mark onboarding complete on the backend
    try {
      await axios.post(`${BACKEND_URL}/api/auth/complete-onboarding`, {}, { headers });
    } catch (err) {
      console.error('complete-onboarding error:', err);
    }

    // Save legal-watch preferences (secondary — must not block redirect on failure)
    if (selectedDomains.length > 0) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/legal-watch/preferences`,
          { tracked_domains: selectedDomains, notification_enabled: true },
          { headers }
        );
      } catch (err) {
        console.error('legal-watch preferences error:', err);
      }
    }

    // Always reflect completion locally and redirect, even if an API call failed
    const stored = JSON.parse(localStorage.getItem('lawliet_user') || '{}');
    localStorage.setItem('lawliet_user', JSON.stringify({ ...stored, onboarding_completed: true }));
    if (onComplete) onComplete();
    setLoading(false);
    navigate(redirectPath);
  };

  // ── Step 1 : Profil ──────────────────────────────────────────────────────
  const Step1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl font-semibold text-gray-900 tracking-tight">Votre profil juridique</h2>
        <p className="text-gray-500 mt-1">Ces informations personnalisent votre expérience Lawliet.ai.</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Vos spécialités principales
        </label>
        <p className="text-xs text-gray-400 mb-3">Plusieurs choix possibles</p>
        <div className="grid grid-cols-2 gap-2">
          {SPECIALITES.map((s) => (
            <button
              key={s}
              onClick={() => toggleSpecialite(s)}
              className={`px-3 py-2.5 text-sm rounded-lg border text-left flex items-center justify-between transition-all ${
                specialites.includes(s)
                  ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                  : 'border-gray-200 hover:border-blue-200 text-gray-700'
              }`}
            >
              <span>{s}</span>
              {specialites.includes(s) && (
                <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2 pt-2">
        <button
          onClick={() => setStep(2)}
          disabled={specialites.length === 0}
          className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          Continuer <ArrowRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => setStep(2)}
          className="w-full py-2 text-sm text-gray-400 hover:text-gray-600"
        >
          Passer cette étape
        </button>
      </div>
    </div>
  );

  // ── Step 2 : Premier document ────────────────────────────────────────────
  const Step2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl font-semibold text-gray-900 tracking-tight">Importez votre premier document</h2>
        <p className="text-gray-500 mt-1">
          Lawliet.ai analyse contrats, jugements et actes en quelques secondes.
        </p>
      </div>
      <div
        onClick={() => navigate('/documents')}
        className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all group"
      >
        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
          <Upload className="h-7 w-7 text-blue-600" />
        </div>
        <p className="font-medium text-gray-800">Importer un document maintenant</p>
        <p className="text-sm text-gray-400 mt-1">PDF, DOCX, TXT — jusqu'à 50 Mo</p>
      </div>
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <Zap className="h-5 w-5 text-blue-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-gray-800">Ce que l'IA fait automatiquement</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Extraction des clauses clés · Score de risque · Résumé exécutif · Suggestions d'amélioration
            </p>
          </div>
        </div>
      </div>
      <NavButtons onBack={() => setStep(1)} onNext={() => setStep(3)} />
    </div>
  );

  // ── Step 3 : Veille légale ───────────────────────────────────────────────
  const Step3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl font-semibold text-gray-900 tracking-tight">Configurez votre veille légale</h2>
        <p className="text-gray-500 mt-1">
          Recevez les nouvelles lois, règlements et jurisprudence dans vos domaines.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {LEGAL_DOMAINS.map((d) => (
          <button
            key={d.id}
            onClick={() => toggleDomain(d.id)}
            className={`px-3 py-2.5 text-sm rounded-lg border text-left flex items-center justify-between transition-all ${
              selectedDomains.includes(d.id)
                ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                : 'border-gray-200 hover:border-blue-200 text-gray-700'
            }`}
          >
            <span>{d.label}</span>
            {selectedDomains.includes(d.id) && (
              <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
            )}
          </button>
        ))}
      </div>
      <NavButtons onBack={() => setStep(2)} onNext={() => setStep(4)} />
    </div>
  );

  // ── Step 4 : Inviter l'équipe ────────────────────────────────────────────
  const Step4 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl font-semibold text-gray-900 tracking-tight">Invitez votre équipe</h2>
        <p className="text-gray-500 mt-1">
          Collaborez avec vos confrères sur les mêmes dossiers.
        </p>
      </div>
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
        <p className="text-sm font-medium text-blue-800 mb-1">Gestion des invitations</p>
        <p className="text-sm text-blue-600">
          Vous pouvez inviter des collaborateurs depuis{' '}
          <span className="font-medium">Paramètres → Gestion du cabinet</span> à tout moment.
        </p>
        <button
          onClick={() => navigate('/cabinet')}
          className="mt-3 text-sm font-medium text-blue-700 hover:text-blue-800 flex items-center gap-1"
        >
          Gérer le cabinet maintenant <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-800 mb-1">Rôles disponibles</p>
        <ul className="space-y-1">
          <li><span className="font-medium">Admin</span> — gestion complète du cabinet</li>
          <li><span className="font-medium">Avocat / Juriste</span> — accès total aux fonctionnalités</li>
          <li><span className="font-medium">Relecteur</span> — lecture et révision uniquement</li>
          <li><span className="font-medium">Stagiaire</span> — accès limité en lecture</li>
        </ul>
      </div>
      <NavButtons onBack={() => setStep(3)} onNext={() => setStep(5)} />
    </div>
  );

  // ── Step 5 : Prêt ────────────────────────────────────────────────────────
  const Step5 = () => (
    <div className="space-y-6 text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle2 className="h-10 w-10 text-green-600" />
      </div>
      <div>
        <h2 className="font-serif text-3xl font-semibold text-gray-900 tracking-tight">
          Tout est prêt, {user?.full_name?.split(' ')[0] || 'Maître'} !
        </h2>
        <p className="text-gray-500 mt-2">
          Votre espace Lawliet.ai est configuré. Par où voulez-vous commencer ?
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3 text-left">
        {[
          { icon: MessageSquare, title: 'Assistant IA', desc: 'Posez vos premières questions juridiques', href: '/chat' },
          { icon: FolderOpen, title: 'Dossiers', desc: 'Créez votre premier dossier client', href: '/dossiers' },
          { icon: FileText, title: 'Documents', desc: 'Analysez un contrat ou acte', href: '/documents' },
        ].map((item) => {
          const Icon = item.icon;
          return (
          <button
            key={item.title}
            onClick={() => completeOnboarding(item.href)}
            disabled={loading}
            className="p-4 border border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all text-left"
          >
            <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50">
              <Icon className="h-5 w-5 text-blue-600" />
            </div>
            <p className="font-medium text-sm text-gray-900">{item.title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
          </button>
          );
        })}
      </div>
      <button
        onClick={() => completeOnboarding('/dashboard')}
        disabled={loading}
        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
      >
        {loading ? 'Chargement...' : 'Accéder au tableau de bord'}
        {!loading && <ArrowRight className="h-4 w-4" />}
      </button>
    </div>
  );

  const NavButtons = ({ onBack, onNext, nextLabel = 'Continuer', nextDisabled = false }) => (
    <div className="space-y-2 pt-2">
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="px-5 py-3 border border-gray-200 text-gray-600 rounded-xl font-medium flex items-center gap-2 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
        <button
          onClick={onNext}
          disabled={nextDisabled}
          className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
        >
          {nextLabel} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      <button
        onClick={onNext}
        className="w-full py-2 text-sm text-gray-400 hover:text-gray-600"
      >
        Passer cette étape
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="ta-rise relative bg-white rounded-2xl shadow-[0_24px_60px_-24px_rgba(30,58,138,0.35)] ring-1 ring-slate-900/5 max-w-lg w-full p-8 overflow-hidden">
        {/* Top accent */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-gold-500" />
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-xl font-bold text-gray-900">
            Lawliet<span className="text-blue-600">.ai</span>
          </span>
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
            Étape {step} / {TOTAL_STEPS}
          </span>
        </div>

        {/* Progress */}
        <ProgressBar step={step} total={TOTAL_STEPS} />

        {/* Content */}
        {step === 1 && <Step1 />}
        {step === 2 && <Step2 />}
        {step === 3 && <Step3 />}
        {step === 4 && <Step4 />}
        {step === 5 && <Step5 />}
      </div>
    </div>
  );
};

export default Onboarding;
